import { Technology, parseTechnology } from '@common/technology.enum';
import {
  CreateLibraryCommand,
  CreateLibraryNGSCommand,
  CreateLibraryMicroarrayCommand,
  CreateLibraryMLPACommand,
  CreateLibraryTripletExpansionCommand
} from '../commands/library-create-command';
import { LibrarySpecimenInput } from '../base/library-specimen.model';
import { BatchInfo } from '../shared/batch-info.model';

/**
 * Legacy request format
 *
 * Note: Prefer CreateLibraryCommand for new code.
 */
export interface LegacyCreateLibraryRequest {
  createdByUserId?: string;
  cellId?: string;
  technology: string;
  samples: LegacyCreateLibrarySample[];
  libraryGroups?: LegacyLibraryGroup[];
}

/**
 * Legacy sample format
 *
 * Note: Prefer LibrarySpecimenInput for new code.
 */
export interface LegacyCreateLibrarySample {
  lgCode: string;
  indexName: string;
  indexAdapterId?: string;
  sequence?: string;
  uploadMethod: string;
  position?: string;
}

/**
 * Legacy library group format
 *
 * Note: Prefer technology-specific commands for new code.
 */
export interface LegacyLibraryGroup {
  libraryCode?: string;
  chipType?: string;
  arrangementType?: string;
  plateName?: string;
  chipLot?: string;
  chipReference?: string;
  creationDate?: string;
  lot?: string;
  reference?: string;
  equipment?: string;
  samples: LegacyCreateLibrarySample[];
}

/**
 * Adapter to convert legacy requests to new Command pattern.
 * This allows gradual migration from the old format to the new typed commands.
 *
 * Note: This adapter exists only for backward compatibility during migration.
 */
export class LibraryLegacyAdapter {
  private constructor() {
    throw new Error('LibraryLegacyAdapter is a utility class');
  }

  /**
   * Converts a legacy request to a typed CreateLibraryCommand
   *
   * @param request - Legacy request format
   * @returns Typed command for the appropriate technology
   */
  static toCommand(request: LegacyCreateLibraryRequest): CreateLibraryCommand {
    const technology = parseTechnology(request.technology);
    if (!technology) {
      throw new Error(`Invalid technology: ${request.technology}`);
    }

    // Extract library code and samples from the first group or directly
    const group = request.libraryGroups?.[0];
    // Backend generates libraryCode automatically if not provided
    // Use a temporary placeholder to pass validation
    const libraryCode = group?.libraryCode || 'PENDING_GENERATION';
    const legacySamples = group?.samples ?? request.samples;

    const specimens = LibraryLegacyAdapter.toSpecimenInputs(legacySamples);

    switch (technology) {
      case Technology.NGS:
        return LibraryLegacyAdapter.toNGSCommand(
          request,
          group,
          libraryCode,
          specimens
        );

      case Technology.MICROARREGLOS:
        return LibraryLegacyAdapter.toMicroarrayCommand(
          request,
          group,
          libraryCode,
          specimens
        );

      case Technology.MLPA:
        return LibraryLegacyAdapter.toMLPACommand(
          request,
          group,
          libraryCode,
          specimens
        );

      case Technology.EXPANSION_TRIPLETAS:
        return LibraryLegacyAdapter.toTripletCommand(
          request,
          group,
          libraryCode,
          specimens
        );

      default:
        throw new Error(`Unsupported technology: ${technology}`);
    }
  }

  private static toSpecimenInputs(
    samples: LegacyCreateLibrarySample[]
  ): LibrarySpecimenInput[] {
    return samples.map((s) => ({
      lgCode: s.lgCode,
      indexAdapterId: s.indexAdapterId ?? null,
      position: s.position ?? null,
      uploadMethod: s.uploadMethod ?? null
    }));
  }

  private static toNGSCommand(
    request: LegacyCreateLibraryRequest,
    group: LegacyLibraryGroup | undefined,
    libraryCode: string,
    specimens: LibrarySpecimenInput[]
  ): CreateLibraryNGSCommand {
    return {
      technology: Technology.NGS,
      libraryCode,
      cellId: request.cellId ?? '',
      plateId: (group as any)?.plateId,
      indexId: (group as any)?.indexId,
      specimens
    };
  }

  private static toMicroarrayCommand(
    request: LegacyCreateLibraryRequest,
    group: LegacyLibraryGroup | undefined,
    libraryCode: string,
    specimens: LibrarySpecimenInput[]
  ): CreateLibraryMicroarrayCommand {
    return {
      technology: Technology.MICROARREGLOS,
      libraryCode,
      chipType: group?.chipType ?? '',
      arrangementType: group?.arrangementType ?? '',
      plateName: group?.plateName,
      batchInfo: LibraryLegacyAdapter.toBatchInfo(
        group?.chipLot,
        group?.chipReference
      ),
      specimens
    };
  }

  private static toMLPACommand(
    request: LegacyCreateLibraryRequest,
    group: LegacyLibraryGroup | undefined,
    libraryCode: string,
    specimens: LibrarySpecimenInput[]
  ): CreateLibraryMLPACommand {
    return {
      technology: Technology.MLPA,
      libraryCode,
      creationDate: group?.creationDate
        ? new Date(group.creationDate)
        : undefined,
      plateName: group?.plateName,
      equipment: group?.equipment,
      batchInfo: LibraryLegacyAdapter.toBatchInfo(group?.lot, group?.reference),
      specimens
    };
  }

  private static toTripletCommand(
    request: LegacyCreateLibraryRequest,
    group: LegacyLibraryGroup | undefined,
    libraryCode: string,
    specimens: LibrarySpecimenInput[]
  ): CreateLibraryTripletExpansionCommand {
    return {
      technology: Technology.EXPANSION_TRIPLETAS,
      libraryCode,
      creationDate: group?.creationDate
        ? new Date(group.creationDate)
        : undefined,
      plateName: group?.plateName,
      equipment: group?.equipment,
      batchInfo: LibraryLegacyAdapter.toBatchInfo(group?.lot, group?.reference),
      specimens
    };
  }

  private static toBatchInfo(
    lot?: string,
    reference?: string
  ): BatchInfo | undefined {
    if (!lot && !reference) {
      return undefined;
    }
    return {
      lot: lot ?? null,
      reference: reference ?? null,
      position: null
    };
  }
}
